/**
 * Kanban (R4) - sloupce = brokerage stage (packages/case/src/internal/state-machine.ts
 * `BrokerageStage`, jen ceske popisky pro zobrazeni). Poradi je kosmeticke
 * (leva->prava dle pipeline); SKUTECNE povolene prechody chodi z API
 * (`allowedTransitions` na CaseDto) - tady se state machine neduplikuje.
 *
 * INVARIANT: seznam musi obsahovat KAZDOU fazi stavoveho automatu, jinak
 * zakazka v te fazi ze seznamu zmizi. Fazi `audit` pridal krok S1 (spusteni
 * auditu prevede zakazku z `intake`) a krok S3 ji sem doplnil — bez ni byla
 * zakazka hned po spusteni auditu neviditelna (nalez z e2e prubehu).
 */
export const KANBAN_STAGE_ORDER = [
  'intake',
  'audit',
  'preparation',
  'listing',
  'viewings',
  'negotiation',
  'reservation',
  'contracting',
  'handover',
  'closed',
] as const;

export const CANCELLED_STAGE = 'cancelled';

export const STAGE_LABELS: Readonly<Record<string, string>> = {
  intake: 'Příjem',
  audit: 'Audit',
  preparation: 'Příprava',
  listing: 'Inzerce',
  viewings: 'Prohlídky',
  negotiation: 'Jednání',
  reservation: 'Rezervace',
  contracting: 'Smlouvy',
  handover: 'Předání',
  closed: 'Uzavřeno',
  cancelled: 'Zrušené',
};

export function stageLabel(stage: string): string {
  return STAGE_LABELS[stage] ?? stage;
}

/**
 * Krok GRAF3 — ODSTIN FAZE. JEDEN ZDROJ pro kapsli v seznamu i pro tecku
 * v zahlavi sloupce kanbanu (driv to byly TRI tabulky za sebou —
 * `STAGE_BADGE_CLASS` → `BADGE_CLASS_A2` / `DOT_CLASS_A2`; tahle jedna je
 * nahrazuje vsechny a ty zanikly).
 *
 * PROC UZ NEJDE PRES SEMANTICKY TON: `rh-badge--neutral/accent/warning/…`
 * rika „ceka na nekoho", „hotovo", „chyba" — to je pojem FAKTURY nebo
 * POPTAVKY, ne faze zakazky, a jeho odstiny ma administrace dal
 * (`.rh-a2-badge--ok/wait/bad`). `--rh-stage-*` je IDENTITA FAZE: predloha
 * seznamu
 * (`/srv/debata/maklerov-grafika-zakazky/VYSLEDEK.png`) dava kazde fazi
 * vlastni odstin, protoze makler ma na radku poznat, KTERA faze to je. Do
 * GRAF3 sdilely Inzerce, Prohlídky i Jednání jeden olivovy ton a rozeznat je
 * nesly.
 *
 * Faze, ktere predloha nekresli, se pripojuji k nejblizsi kreslene — a to
 * dopredu, ne dozadu, aby odstin nikdy nesliboval vic, nez kde zakazka je:
 * `intake`/`preparation` k Auditu, `reservation` k Jednání, `handover`
 * ke Smlouvam. `cancelled` zustava na `--rh-danger-*` (viz tokeny).
 */
const STAGE_HUE: Readonly<Record<string, string>> = {
  intake: 'audit',
  audit: 'audit',
  preparation: 'audit',
  listing: 'listing',
  viewings: 'viewings',
  negotiation: 'negotiation',
  reservation: 'negotiation',
  contracting: 'contracting',
  handover: 'contracting',
  closed: 'closed',
};

/** `null` = faze zadny vlastni odstin nema (zrusena zakazka, nezname jmeno). */
export function stageHue(stage: string): string | null {
  return STAGE_HUE[stage] ?? null;
}

/**
 * Krok GRAF5 — KROKOVNIK PETI FAZI v hlavicce detailu a nad seznamem
 * (predloha `/srv/debata/maklerov-grafika-detail`: Audit · Inzerce ·
 * Prohlídky · Jednání · Smlouvy).
 *
 * Neni to sesty vycet fazi: je to presne ta rada, kterou uz urcuje `STAGE_HUE`
 * (deset stavu automatu → pet odstinu). Vlastni tabulka by znamenala, ze
 * krokovnik a kapsle v seznamu mohou o teze zakazce rict jine misto v rade.
 * `closed` neni krok — je to „za poslednim krokem", takze cely krokovnik
 * svití hotovy; `cancelled` a nezname jmeno nema krok ZADNY (0 hotovych),
 * protoze o postupu takove zakazky nevime nic.
 */
export const STAGE_STEP_HUES = ['audit', 'listing', 'viewings', 'negotiation', 'contracting'] as const;

export type StageStepState = 'done' | 'now' | 'wait';

export interface CaseStageStep {
  /** Klic odstinu — zaroven `data-testid` a trida `--stage-*`. */
  readonly id: (typeof STAGE_STEP_HUES)[number];
  readonly label: string;
  readonly state: StageStepState;
}

export function caseStageSteps(stage: string | null): readonly CaseStageStep[] {
  const hue = stage === null ? null : stageHue(stage);
  const index =
    hue === 'closed'
      ? STAGE_STEP_HUES.length
      : hue === null
        ? -1
        : STAGE_STEP_HUES.indexOf(hue as (typeof STAGE_STEP_HUES)[number]);
  return STAGE_STEP_HUES.map((id, i) => ({
    id,
    /* Popisek je TYZ katalog, jaky nese kapsle faze (`STAGE_LABELS`). */
    label: stageLabel(id),
    state: i < index ? 'done' : i === index ? 'now' : 'wait',
  }));
}

export function stageBadgeClassA2(stage: string): string {
  const hue = stageHue(stage);
  if (hue !== null) return `rh-a2-badge--stage-${hue}`;
  return stage === CANCELLED_STAGE ? 'rh-a2-badge--bad' : '';
}

/** Barevna tecka v zahlavi sloupce kanbanu (mockup `.kcol__head .dot`). */
export function stageDotClassA2(stage: string): string {
  const hue = stageHue(stage);
  if (hue !== null) return `rh-a2-dot--stage-${hue}`;
  return stage === CANCELLED_STAGE ? 'rh-a2-dot--bad' : '';
}

/** Kanban je scenar pro brokerage pipeline (viz zadani) - acquisition/pm maji vlastni stavovy prostor. */
export const KANBAN_CASE_TYPES = ['brokerage', 'brokerage_sale', 'brokerage_rent'] as const;
export type KanbanCaseType = (typeof KANBAN_CASE_TYPES)[number];

export const CASE_TYPE_LABELS: Readonly<Record<KanbanCaseType, string>> = {
  brokerage: 'Obecné',
  brokerage_sale: 'Prodej',
  brokerage_rent: 'Pronájem',
};

export type SlaStatus = 'ok' | 'blizi_se' | 'prosle' | 'none';

const SLA_WARNING_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;

export function slaStatus(
  slaDueAt: string | null,
  slaBreachedAt: string | null,
  closedAt: string | null,
  now: Date,
): SlaStatus {
  if (closedAt !== null || slaDueAt === null) return 'none';
  if (slaBreachedAt !== null) return 'prosle';
  const due = new Date(slaDueAt);
  if (due.getTime() < now.getTime()) return 'prosle';
  if (due.getTime() - now.getTime() <= SLA_WARNING_WINDOW_MS) return 'blizi_se';
  return 'ok';
}

export const SLA_STATUS_LABELS: Readonly<Record<SlaStatus, string>> = {
  ok: 'SLA OK',
  blizi_se: 'SLA se blíží',
  prosle: 'SLA prošlo',
  none: '',
};

export const SLA_STATUS_BADGE_CLASS: Readonly<Record<SlaStatus, string>> = {
  ok: 'rh-badge--success',
  blizi_se: 'rh-badge--warning',
  prosle: 'rh-badge--danger',
  none: 'rh-badge--neutral',
};

/** Krok UXR1 — tytez stavy ve stitcich noveho vzhledu. */
export const SLA_STATUS_BADGE_CLASS_A2: Readonly<Record<SlaStatus, string>> = {
  ok: 'rh-a2-badge--ok',
  blizi_se: 'rh-a2-badge--wait',
  prosle: 'rh-a2-badge--bad',
  none: '',
};

/**
 * Krok TAB1 body 6/7/9 — „STOJÍ DÉLE NEŽ 30 DNÍ“ (hlavicka, cerveny filtr,
 * cervene cislo na karte). Krok AS3: podminka zije v `@reos/today-metrics`,
 * protoze ji tytez slovy pocita i kontrola Asistentky `reos.run_checks`
 * (`case_stuck`) — jedna funkce, ne dve kopie, ktere by se rozesly.
 */
import { daysInStage, STAGE_STUCK_DAYS, isStuckInStage } from '@reos/today-metrics';

export { daysInStage, STAGE_STUCK_DAYS, isStuckInStage };
